import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Screen, JournalEntry, LifeContext, Decision, Pattern, AlignmentFilter, FeatureDiscoveryState, AIPersonalityKey } from '../types';
import HomeView from '../views/HomeView';
import JournalView from '../views/JournalView';
import AskView from '../views/AskView';
import OnboardingView from '../views/OnboardingView';
import InsightsView from '../views/InsightsView';
import DecisionsView from '../views/DecisionsView';
import MyProfileView from '../views/MyProfileView';
import EvolutionHistoryView from '../views/EvolutionHistoryView';
import LandingView from '../views/LandingView';
import LoginView from '../views/LoginView';
import RegisterView from '../views/RegisterView';
import PrivacyView from '../views/PrivacyView';
import TermsView from '../views/TermsView';
import EmailPreferencesView from '../views/EmailPreferencesView';
import ConsentGate from '../components/ConsentGate';
import AnalysisLoadingOverlay from '../components/AnalysisLoadingOverlay';
import { analyzeMoment } from '../services/openaiService';
import { detectPatterns, findSemanticLinks, extractPeople, updatePatternStrengths } from '../services/graphService';
import { UI_STRINGS } from '../constants/translations';
import { LayoutGrid, BookOpen, MessageSquare, Compass, BarChart3, User } from 'lucide-react';
import { Language } from '../types';
import { initAnalyticsConsent } from './lib/analytics';
import { FeatureDiscoveryProvider } from '../contexts/FeatureDiscoveryContext';
import { LanguageProvider } from '../contexts/LanguageContext';

const landingToAppLanguage: Record<'en' | 'it' | 'es' | 'de' | 'fr', Language> = {
  en: 'English',
  it: 'Italian',
  es: 'Spanish',
  de: 'German',
  fr: 'French'
};

type LandingPage = 'landing' | 'login' | 'register' | 'privacy' | 'terms' | 'email-preferences';
type ConsentPage = 'consent' | 'privacy' | 'terms';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: false,
    },
  },
});

async function apiRequest(url: string, options?: RequestInit) {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

function useAuth() {
  const { data: user, isLoading, error } = useQuery({
    queryKey: ["/api/auth/user"],
    queryFn: async () => {
      const response = await fetch("/api/auth/user", { credentials: "include" });
      if (!response.ok) {
        if (response.status === 401) return null;
        throw new Error("Failed to fetch user");
      }
      return response.json();
    },
    retry: false,
  });

  return { user, isLoading, isAuthenticated: !!user, error };
}

const MainApp: React.FC = () => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const qc = useQueryClient();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [isHydratingPostOnboarding, setIsHydratingPostOnboarding] = useState(false);
  const [hydrationProgress, setHydrationProgress] = useState(0);
  // Check URL for email preferences page (public access via token)
  const [landingPage, setLandingPage] = useState<LandingPage>(() => {
    if (typeof window !== 'undefined' && window.location.pathname === '/email-preferences') {
      return 'email-preferences';
    }
    return 'landing';
  });
  const [emailPrefsToken, setEmailPrefsToken] = useState<string | null>(() => {
    if (typeof window !== 'undefined' && window.location.pathname === '/email-preferences') {
      const params = new URLSearchParams(window.location.search);
      return params.get('token');
    }
    return null;
  });
  const [consentPage, setConsentPage] = useState<ConsentPage>('consent');
  const [consentsAccepted, setConsentsAccepted] = useState(false);

  // Queue system for photo uploads - process one at a time
  const uploadQueueRef = useRef<Promise<any>>(Promise.resolve());
  const [landingLang, setLandingLang] = useState<'en' | 'it' | 'es' | 'de' | 'fr'>(() => {
    const savedLang = localStorage.getItem('egovex_preferred_language');
    if (savedLang && ['en', 'it', 'es', 'de', 'fr'].includes(savedLang)) {
      return savedLang as 'en' | 'it' | 'es' | 'de' | 'fr';
    }
    const browserLang = navigator.language.split('-')[0];
    if (['en', 'it', 'es', 'de', 'fr'].includes(browserLang)) {
      return browserLang as 'en' | 'it' | 'es' | 'de' | 'fr';
    }
    return 'en';
  });

  const { data: dbContext, isFetched: contextFetched, isLoading: contextLoading } = useQuery({
    queryKey: ['/api/context'],
    queryFn: () => apiRequest('/api/context'),
    enabled: isAuthenticated,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  // Paginated entries - load first 5 quickly, then rest
  const [entriesPage, setEntriesPage] = useState(0);
  const [allEntriesLoaded, setAllEntriesLoaded] = useState(false);
  const [firstPageLoaded, setFirstPageLoaded] = useState(false);
  const PAGE_SIZE = 5;

  const { data: dbEntriesData, isFetched: entriesFetched, isLoading: entriesLoading } = useQuery({
    queryKey: ['/api/entries', entriesPage],
    queryFn: () => apiRequest(`/api/entries?limit=${PAGE_SIZE}&offset=${entriesPage * PAGE_SIZE}`),
    enabled: isAuthenticated,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
  
  // Extract entries array from paginated response (backwards compatible)
  const dbEntries = useMemo(() => {
    if (!dbEntriesData) return undefined;
    // Handle new paginated format { entries, total, hasMore }
    if (dbEntriesData.entries && Array.isArray(dbEntriesData.entries)) {
      return dbEntriesData.entries;
    }
    // Fallback for old format (direct array)
    if (Array.isArray(dbEntriesData)) {
      return dbEntriesData;
    }
    return [];
  }, [dbEntriesData]);
  
  const hasMoreEntries = dbEntriesData?.hasMore ?? false;
  const totalEntries = dbEntriesData?.total ?? 0;


  const { data: dbDecisions } = useQuery({
    queryKey: ['/api/decisions'],
    queryFn: () => apiRequest('/api/decisions'),
    enabled: isAuthenticated,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const { data: dbPatterns } = useQuery({
    queryKey: ['/api/patterns'],
    queryFn: () => apiRequest('/api/patterns'),
    enabled: isAuthenticated,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const saveContextMutation = useMutation({
    mutationFn: (ctx: Partial<LifeContext>) => apiRequest('/api/context', { method: 'POST', body: JSON.stringify(ctx) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/api/context'] }),
  });

  const savePatternsMutation = useMutation({
    mutationFn: (p: Pattern[]) => apiRequest('/api/patterns', { method: 'POST', body: JSON.stringify(p) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/api/patterns'] }),
  });

  const [currentScreen, setCurrentScreen] = useState<Screen>('onboarding');
  const [alignmentFilter, setAlignmentFilter] = useState<AlignmentFilter>(null);
  const [lifeContext, setLifeContext] = useState<LifeContext>({
    userName: '', goals: [], values: [], blockers: [], setupComplete: false, language: 'English'
  });
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [lastPatternAnalysis, setLastPatternAnalysis] = useState<number>(0);
  
  const handleNavigateWithFilter = (screen: Screen, filter?: AlignmentFilter) => {
    setAlignmentFilter(filter || null);
    setCurrentScreen(screen);
  };
  
  // Auto-refresh context when navigating to profile or evolution screens
  useEffect(() => {
    if (isAuthenticated && (currentScreen === 'profile' || currentScreen === 'evolution')) {
      qc.invalidateQueries({ queryKey: ['/api/context'] });
    }
  }, [currentScreen, isAuthenticated, qc]);

  // Shared mapper functions to ensure consistent state hydration
  const mapDbEntriesToState = useCallback((dbEntries: any[]) => {
    return dbEntries
      .filter((e: any) => e && e.id) // Filter out invalid entries
      .map((e: any) => {
        const ts = e.timestamp ? new Date(e.timestamp).getTime() : (e.createdAt ? new Date(e.createdAt).getTime() : Date.now());
        const ga = typeof e.goalAlignment === 'number' ? e.goalAlignment : 50;
        return {
          id: e.id,
          timestamp: ts,
          content: e.content || '',
          type: e.type || 'moment',
          emotion: e.emotion || '✨',
          aiInsight: e.aiInsight || '',
          alignment: typeof e.alignment === 'number' ? e.alignment : 50,
          tags: Array.isArray(e.tags) ? e.tags : [],
          category: e.category || 'Focus',
          goalAlignment: ga,
          imageUrl: e.imageUrl || undefined,
          relatedTo: Array.isArray(e.relatedTo) ? e.relatedTo : [],
          people: Array.isArray(e.people) ? e.people : [],
          places: Array.isArray(e.places) ? e.places : [],
          dimensionImpacts: e.dimensionImpacts || {},
          emotionalTriggers: Array.isArray(e.emotionalTriggers) ? e.emotionalTriggers : [],
          timeOfDay: e.timeOfDay || undefined,
          dayOfWeek: typeof e.dayOfWeek === 'number' ? e.dayOfWeek : undefined,
          // IMPORTANT: Always set isAnalyzing to false for entries loaded from DB
          // This prevents grey overlay persistence on entries that were interrupted during analysis
          isAnalyzing: false,
          // Preserve isLiked status from database
          isLiked: e.isLiked || false,
        };
      });
  }, []);

  const mapDbDecisionsToState = useCallback((dbDecisions: any[]) => {
    return dbDecisions.map((d: any) => ({
      id: d.id,
      title: d.title || 'Untitled',
      context: d.context,
      analysisSummary: d.analysisSummary,
      options: d.options || [],
      bestOption: d.bestOption,
      reasonWhy: d.reasonWhy,
      nextAction: d.nextAction,
      status: d.status || 'pending',
      timestamp: d.timestamp ? new Date(d.timestamp).getTime() : Date.now(),
    }));
  }, []);

  const mapDbContextToState = useCallback((ctx: any): LifeContext => ({
    userName: ctx.userName || '',
    goals: ctx.goals || [],
    values: ctx.values || [],
    blockers: ctx.blockers || [],
    setupComplete: ctx.setupComplete || false,
    language: ctx.language || 'English',
    aiPersonality: ctx.aiPersonality,
    featureDiscovery: ctx.featureDiscovery || {},
    aiProfile: ctx.aiProfile,
    keyPeople: ctx.keyPeople || [],
    keyPlaces: ctx.keyPlaces || [],
    selectedPersonalityKey: ctx.selectedPersonalityKey,
  }), []);

  // Compute effective entries - prefer local state, fall back to dbEntries
  // Also track loading state for UI
  const isDataLoading = entriesLoading || contextLoading;
  
  const effectiveEntries = useMemo(() => {
    // If we have local entries (either from DB sync or new ones), use them
    if (entries.length > 0) {
      return entries;
    }
    // Fall back to dbEntries if local state is empty but DB has data
    if (Array.isArray(dbEntries) && dbEntries.length > 0) {
      return mapDbEntriesToState(dbEntries);
    }
    return [];
  }, [entries, dbEntries, mapDbEntriesToState]);

  // Sync lifeContext when dbContext updates (after initial load)
  useEffect(() => {
    if (dataLoaded && dbContext && dbContext.setupComplete) {
      setLifeContext(mapDbContextToState(dbContext));
    }
  }, [dbContext, dataLoaded, mapDbContextToState]);

  useEffect(() => {
    initAnalyticsConsent();
  }, []);

  // Sync landingLang with user's stored preferredLanguage when logged in
  useEffect(() => {
    if (user?.preferredLanguage) {
      const userLang = user.preferredLanguage as 'en' | 'it' | 'es' | 'de' | 'fr';
      if (['en', 'it', 'es', 'de', 'fr'].includes(userLang)) {
        setLandingLang(userLang);
        localStorage.setItem('egovex_preferred_language', userLang);
      }
    }
  }, [user?.preferredLanguage]);

  // Track if initial sync has been done to avoid overwriting local entries
  const [entriesSynced, setEntriesSynced] = useState(false);

  // Clear state on logout to prevent data leakage between users
  useEffect(() => {
    if (!isAuthenticated) {
      setEntries([]);
      setDecisions([]);
      setPatterns([]);
      setEntriesSynced(false);
      setDataLoaded(false);
      setEntriesPage(0);
      setAllEntriesLoaded(false);
      setFirstPageLoaded(false);
      setLifeContext({
        userName: '', goals: [], values: [], blockers: [], setupComplete: false, language: 'English'
      });
    }
  }, [isAuthenticated]);
  
  // Function to load more entries
  const loadMoreEntries = useCallback(() => {
    if (hasMoreEntries && !entriesLoading) {
      setEntriesPage(prev => prev + 1);
    }
  }, [hasMoreEntries, entriesLoading]);
  
  // Track when first page of entries is loaded (regardless of which page we're on now)
  useEffect(() => {
    if (entriesPage === 0 && entriesFetched && !entriesLoading && !firstPageLoaded) {
      console.log('📄 First page of entries loaded');
      setFirstPageLoaded(true);
    }
  }, [entriesPage, entriesFetched, entriesLoading, firstPageLoaded]);

  // Auto-load ALL remaining entries in background after first page
  useEffect(() => {
    if (!entriesFetched || allEntriesLoaded) return;

    // Continue loading while there are more entries
    if (hasMoreEntries) {
      const timer = setTimeout(() => {
        setEntriesPage(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timer);
    } else if (entriesPage > 0) {
      // No more entries to load - mark as complete
      setAllEntriesLoaded(true);
    }
  }, [entriesFetched, hasMoreEntries, allEntriesLoaded, entriesPage]);

  // PAGINATED DATA LOADING: Accumulate entries from multiple pages
  // Preserves entries that are actively analyzing (isAnalyzing: true)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Load entries whenever dbEntries changes and has data
    if (Array.isArray(dbEntries) && dbEntries.length > 0) {
      const mapped = mapDbEntriesToState(dbEntries);
      const newIds = new Set(mapped.map(e => e.id));
      
      setEntries(prevEntries => {
        // Keep actively analyzing entries
        const activelyAnalyzing = prevEntries.filter(e => 
          e.isAnalyzing && !newIds.has(e.id)
        );
        
        // Keep existing DB entries that aren't in the new batch (for pagination)
        const existingDbEntries = prevEntries.filter(e => 
          !e.isAnalyzing && !newIds.has(e.id)
        );
        
        // Combine: analyzing first, then all DB entries sorted by timestamp
        const allDbEntries = [...existingDbEntries, ...mapped];
        allDbEntries.sort((a, b) => b.timestamp - a.timestamp);
        
        return [...activelyAnalyzing, ...allDbEntries];
      });
      
      if (!entriesSynced) {
        setEntriesSynced(true);
      }
    } else if (!entriesSynced && entriesFetched) {
      // DB returned empty but fetch completed - mark as synced for new users
      setEntriesSynced(true);
    }
  }, [isAuthenticated, dbEntries, entriesFetched, mapDbEntriesToState, entriesSynced]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Load decisions whenever dbDecisions changes and has data
    if (Array.isArray(dbDecisions) && dbDecisions.length > 0) {
      const mapped = mapDbDecisionsToState(dbDecisions);
      if (mapped.length !== decisions.length) {
        console.log(`📥 Loading ${mapped.length} decisions from DB`);
        setDecisions(mapped);
      }
    }
  }, [isAuthenticated, dbDecisions, decisions.length, mapDbDecisionsToState]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Load patterns whenever dbPatterns changes and has data
    if (Array.isArray(dbPatterns) && dbPatterns.length > 0 && dbPatterns.length !== patterns.length) {
      console.log(`📥 Loading ${dbPatterns.length} patterns from DB`);
      setPatterns(dbPatterns);
    }
  }, [isAuthenticated, dbPatterns, patterns.length]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Load context and navigate to home when setupComplete
    // NOTE: Do NOT set dataLoaded here - let the entries effect control that
    if (dbContext && dbContext.setupComplete && !lifeContext.setupComplete) {
      console.log(`📥 Loading context from DB, userName: ${dbContext.userName}`);
      setLifeContext(mapDbContextToState(dbContext));
      setCurrentScreen('pulse');
    }
  }, [isAuthenticated, dbContext, lifeContext.setupComplete, mapDbContextToState]);

  // Mark data as loaded when context is fetched (regardless of setupComplete status)
  // This allows new users to see onboarding instead of being stuck on sync overlay
  useEffect(() => {
    if (!isAuthenticated || dataLoaded) return;

    // For new users (setupComplete = false), just need context to be fetched
    // For existing users (setupComplete = true), wait for first entries page to load
    const isNewUser = contextFetched && !dbContext?.setupComplete;
    const contextReady = contextFetched && dbContext?.setupComplete;

    // Use firstPageLoaded flag - this is stable and doesn't change with pagination
    // Also accept if entries are already in state (cached from previous session)
    // Also accept if entriesSynced is true (entries have been processed)
    const entriesReady = firstPageLoaded || entries.length > 0 || entriesSynced;

    // New users: load immediately after context is fetched so they see onboarding
    // Existing users: wait for context + first entries page
    if (isNewUser || (contextReady && entriesReady)) {
      console.log('📊 Data loaded:', { isNewUser, contextReady, firstPageLoaded, entriesInState: entries.length, entriesSynced });
      setDataLoaded(true);
      setSyncProgress(0);
    }
  }, [isAuthenticated, contextFetched, firstPageLoaded, entries.length, entriesSynced, dbContext?.setupComplete, dataLoaded]);

  // Safety timeout: ensure loading overlays don't get stuck
  // If hydration is still running after 45 seconds, force it to complete
  useEffect(() => {
    if (!isHydratingPostOnboarding) return;

    const safetyTimeout = setTimeout(() => {
      console.warn('Safety timeout: forcing hydration overlay to close');
      setIsHydratingPostOnboarding(false);
      setHydrationProgress(0);
      setDataLoaded(true);
    }, 45000); // 45 seconds max

    return () => clearTimeout(safetyTimeout);
  }, [isHydratingPostOnboarding]);

  // Also add safety timeout for sync overlay
  useEffect(() => {
    if (!isAuthenticated || dataLoaded) return;

    const safetyTimeout = setTimeout(() => {
      console.warn('Safety timeout: forcing sync overlay to close');
      setDataLoaded(true);
      setSyncProgress(0);
    }, 30000); // 30 seconds max for sync

    return () => clearTimeout(safetyTimeout);
  }, [isAuthenticated, dataLoaded]);

  useEffect(() => {
    if (!isAuthenticated || !dataLoaded) return;
    const totalContextItems = entries.length + decisions.length;
    if (totalContextItems >= 5 && patterns.length === 0 && lifeContext.setupComplete) {
      triggerPatternAnalysis();
    }
  }, [entries.length, decisions.length, patterns.length, lifeContext.setupComplete, isAuthenticated, dataLoaded]);

  const captureMoment = async (content: string, imageUrl?: string, manualMood?: string) => {
    const tempId = Math.random().toString(36).substr(2, 9);

    // Multilingual loading messages
    const loadingTexts: Record<string, { imageAnalysis: string; reflection: string; listening: string; queued: string }> = {
      Italian: { imageAnalysis: 'Analisi immagine...', reflection: 'Riflessione', listening: 'In ascolto del momento...', queued: 'In coda...' },
      English: { imageAnalysis: 'Analyzing image...', reflection: 'Reflection', listening: 'Listening to the moment...', queued: 'Queued...' },
      Spanish: { imageAnalysis: 'Analizando imagen...', reflection: 'Reflexión', listening: 'Escuchando el momento...', queued: 'En cola...' },
      German: { imageAnalysis: 'Bild analysieren...', reflection: 'Reflexion', listening: 'Den Moment aufnehmen...', queued: 'In Warteschlange...' },
      French: { imageAnalysis: 'Analyse de l\'image...', reflection: 'Réflexion', listening: 'À l\'écoute du moment...', queued: 'En file d\'attente...' },
    };
    const loadingT = loadingTexts[lifeContext.language] || loadingTexts.English;

    const newEntry: JournalEntry = {
      id: tempId,
      timestamp: Date.now(),
      content: content || (imageUrl ? loadingT.imageAnalysis : loadingT.reflection),
      type: 'moment',
      emotion: manualMood || '✨',
      aiInsight: loadingT.listening,
      alignment: 50,
      tags: [],
      category: 'Focus',
      goalAlignment: 50,
      imageUrl: imageUrl,
      isAnalyzing: true,
      relatedTo: [],
      people: [],
      emotionalTriggers: []
    };

    // Use effectiveEntries as base to include DB entries that may not be in local state yet
    setEntries(prev => {
      const baseEntries = prev.length > 0 ? prev : effectiveEntries;
      return [newEntry, ...baseEntries.filter(e => e.id !== tempId)];
    });

    // Queue the actual analysis to process one at a time (prevents server overload)
    // This is especially important for photo uploads which are heavy on the AI
    const processEntry = async () => {
      console.log(`📸 Processing entry ${tempId}${imageUrl ? ' (with image)' : ''}`);

      try {
        const [analysis, semanticLinks, people] = await Promise.all([
          analyzeMoment(content, lifeContext, imageUrl),
          findSemanticLinks(newEntry, effectiveEntries, lifeContext),
          extractPeople(content, imageUrl)
        ]);

        const finalEntry = {
          ...newEntry,
          ...analysis,
          content: content || analysis.title || newEntry.content,
          isAnalyzing: false,
          emotion: manualMood || analysis.emotion || '✨',
          relatedTo: semanticLinks,
          people: people.map(p => ({ name: p.name, relationship: p.relationship })),
          emotionalTriggers: analysis.tags || []
        };

        setEntries(prev => prev.map(e => e.id === tempId ? finalEntry : e));

        try {
          // Calculate time metadata
          const now = new Date();
          const hour = now.getHours();
          const timeOfDay = hour < 6 ? 'night' : hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
          const dayOfWeek = now.getDay();

          const savedEntry = await apiRequest('/api/entries', {
            method: 'POST',
            body: JSON.stringify({
              content: finalEntry.content,
              type: finalEntry.type,
              emotion: finalEntry.emotion,
              aiInsight: finalEntry.aiInsight,
              alignment: finalEntry.alignment,
              tags: finalEntry.tags,
              category: finalEntry.category,
              goalAlignment: finalEntry.goalAlignment,
              imageUrl: finalEntry.imageUrl,
              relatedTo: finalEntry.relatedTo,
              people: finalEntry.people,
              places: finalEntry.places || [],
              emotionalTriggers: finalEntry.emotionalTriggers,
              dimensionImpacts: finalEntry.dimensionImpacts || {},
              timeOfDay,
              dayOfWeek,
            }),
          });
          setEntries(prev => prev.map(e => e.id === tempId ? { ...finalEntry, id: savedEntry.id } : e));
          qc.invalidateQueries({ queryKey: ['/api/entries'] });

          // Invalidate context cache after a short delay to allow profile auto-evolution to complete
          // The server updates the profile asynchronously, so we wait a bit before refetching
          setTimeout(() => {
            qc.invalidateQueries({ queryKey: ['/api/context'] });
          }, 2000);
        } catch (err) {
          console.error('Failed to save entry to database:', err);
        }

        const daysSinceLastAnalysis = (Date.now() - lastPatternAnalysis) / (1000 * 60 * 60 * 24);
        const totalContextItems = entries.length + decisions.length;
        if (totalContextItems >= 3 && (totalContextItems % 3 === 0 || daysSinceLastAnalysis > 3)) {
          triggerPatternAnalysis();
        }

        console.log(`✅ Entry ${tempId} processed successfully`);
        return finalEntry;
      } catch (err) {
        console.error(`❌ Entry ${tempId} processing failed:`, err);
        setEntries(prev => prev.map(e => e.id === tempId ? {
          ...e,
          isAnalyzing: false,
          aiInsight: 'Analisi non disponibile per questo momento.'
        } : e));
        throw err;
      }
    };

    // Chain to the upload queue to process one at a time
    uploadQueueRef.current = uploadQueueRef.current
      .then(() => processEntry())
      .catch(err => {
        console.error('Queue processing error:', err);
        // Don't propagate error to break the chain
      });

    return uploadQueueRef.current;
  };

  const triggerPatternAnalysis = useCallback(async () => {
    try {
      const updatedPatterns = updatePatternStrengths(patterns, entries);
      const newPatterns = await detectPatterns(entries, decisions, lifeContext, updatedPatterns);
      setPatterns(newPatterns);
      setLastPatternAnalysis(Date.now());
      savePatternsMutation.mutate(newPatterns);
    } catch (error) {
      console.error('Pattern analysis failed:', error);
    }
  }, [patterns, entries, decisions, lifeContext, savePatternsMutation]);

  const resolveDecision = async (id: string) => {
    const decision = decisions.find(d => d.id === id);
    if (!decision) return;
    const newStatus = decision.status === 'pending' ? 'resolved' : 'pending';
    setDecisions(prev => prev.map(d => d.id === id ? { ...d, status: newStatus } : d));
    try {
      await apiRequest(`/api/decisions/${id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
      qc.invalidateQueries({ queryKey: ['/api/decisions'] });
    } catch (err) {
      console.error('Failed to update decision:', err);
    }
  };

  const deleteDecision = async (id: string) => {
    setDecisions(prev => prev.filter(d => d.id !== id));
    try {
      await apiRequest(`/api/decisions/${id}`, { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['/api/decisions'] });
    } catch (err) {
      console.error('Failed to delete decision:', err);
    }
  };

  const deleteEntry = async (id: string) => {
    setEntries(prev => prev.filter(e => e.id !== id));
    try {
      await apiRequest(`/api/entries/${id}`, { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['/api/entries'] });
    } catch (err) {
      console.error('Failed to delete entry:', err);
    }
  };

  const handleLikeEntry = async (entryId: string, isLiked: boolean) => {
    try {
      // Optimistically update local state
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, isLiked } : e));

      const res = await fetch(`/api/entries/${entryId}/like`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isLiked }),
        credentials: 'include'
      });

      if (!res.ok) {
        // Revert on error
        setEntries(prev => prev.map(e => e.id === entryId ? { ...e, isLiked: !isLiked } : e));
        console.error('Failed to toggle like');
      } else {
        // Invalidate cache to ensure consistency
        qc.invalidateQueries({ queryKey: ['/api/entries'] });
      }
    } catch (error) {
      // Revert on error
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, isLiked: !isLiked } : e));
      console.error('Error toggling like:', error);
    }
  };

  const handleSetDecisions = useCallback((updater: React.SetStateAction<Decision[]>) => {
    setDecisions((prev) => {
      const newDecisions = typeof updater === 'function' ? updater(prev) : updater;
      const addedDecisions = newDecisions.filter(nd => !prev.some(pd => pd.id === nd.id));
      addedDecisions.forEach(async (decision) => {
        try {
          const saved = await apiRequest('/api/decisions', {
            method: 'POST',
            body: JSON.stringify({
              id: decision.id,
              title: decision.title,
              context: decision.context,
              analysisSummary: decision.analysisSummary,
              options: decision.options,
              bestOption: decision.bestOption,
              reasonWhy: decision.reasonWhy,
              nextAction: decision.nextAction,
              status: decision.status,
            }),
          });
          setDecisions(curr => curr.map(d => d.id === decision.id ? { ...d, id: saved.id } : d));
          qc.invalidateQueries({ queryKey: ['/api/decisions'] });
          // Invalidate context after decision creation to refresh profile (decisions can trigger evolution)
          setTimeout(() => {
            qc.invalidateQueries({ queryKey: ['/api/context'] });
          }, 2000);
        } catch (err) {
          console.error('Failed to save decision:', err);
        }
      });
      return newDecisions;
    });
  }, [qc]);

  const t = useMemo(() => UI_STRINGS[lifeContext.language] || UI_STRINGS.English, [lifeContext.language]);

  const updateFeatureDiscovery = useCallback((key: keyof FeatureDiscoveryState) => {
    const newDiscoveryState = {
      ...lifeContext.featureDiscovery,
      [key]: true
    };
    setLifeContext(prev => ({
      ...prev,
      featureDiscovery: newDiscoveryState
    }));
    saveContextMutation.mutate({ featureDiscovery: newDiscoveryState });
  }, [lifeContext.featureDiscovery, saveContextMutation]);

  const handleAddValue = useCallback((value: string) => {
    setLifeContext(prev => {
      const newValues = [...(prev.values || []).filter(v => v !== value), value];
      const newContext = { ...prev, values: newValues };
      if (prev.setupComplete) {
        saveContextMutation.mutate({
          userName: prev.userName,
          goals: prev.goals,
          values: newValues,
          blockers: prev.blockers,
          setupComplete: prev.setupComplete,
          language: prev.language,
          aiPersonality: prev.aiPersonality,
        });
      }
      return newContext;
    });
  }, [saveContextMutation]);

  const handleAddGoal = useCallback((goal: string) => {
    setLifeContext(prev => {
      const newGoals = [...(prev.goals || []).filter(g => g !== goal), goal];
      const newContext = { ...prev, goals: newGoals };
      if (prev.setupComplete) {
        saveContextMutation.mutate({
          userName: prev.userName,
          goals: newGoals,
          values: prev.values,
          blockers: prev.blockers,
          setupComplete: prev.setupComplete,
          language: prev.language,
          aiPersonality: prev.aiPersonality,
        });
      }
      return newContext;
    });
  }, [saveContextMutation]);

  // Handle AI personality change
  const handlePersonalityChange = useCallback(async (personalityKey: AIPersonalityKey) => {
    setLifeContext(prev => ({
      ...prev,
      selectedPersonalityKey: personalityKey
    }));
    await saveContextMutation.mutateAsync({ selectedPersonalityKey: personalityKey });
  }, [saveContextMutation]);

  const handleLogin = () => {
    setLandingPage('login');
    window.scrollTo(0, 0);
  };

  const handleRegister = () => {
    setLandingPage('register');
    window.scrollTo(0, 0);
  };
  
  const handleAuthSuccess = () => {
    qc.invalidateQueries({ queryKey: ['/api/auth/user'] });
    window.location.reload();
  };

  // Get language for loading overlay - prioritize context language from DB
  const loadingLanguage: Language = (() => {
    // First priority: dbContext.language (user's actual app language setting)
    if (dbContext?.language && ['Italian', 'English', 'Spanish', 'German', 'French'].includes(dbContext.language)) {
      return dbContext.language as Language;
    }
    // Second priority: user's preferred language from auth (ISO code)
    if (user?.preferredLanguage && ['en', 'it', 'es', 'de', 'fr'].includes(user.preferredLanguage)) {
      return landingToAppLanguage[user.preferredLanguage as 'en' | 'it' | 'es' | 'de' | 'fr'];
    }
    // Fallback to landing lang (from localStorage or browser)
    return landingToAppLanguage[landingLang] || 'English';
  })();

  // DON'T show loading overlay during initial auth check for non-authenticated users
  // This prevents the sync overlay from appearing on the public landing page
  // Only show it if we're loading AND we have a user (or during a login flow)

  const handleShowPrivacy = () => {
    setLandingPage('privacy');
    window.scrollTo(0, 0);
  };

  const handleShowTerms = () => {
    setLandingPage('terms');
    window.scrollTo(0, 0);
  };

  const handleBackToLanding = () => {
    setLandingPage('landing');
    window.scrollTo(0, 0);
  };

  // Show nothing while initial auth check is in progress
  // This prevents flash of landing page before authenticated view
  if (isLoading) {
    return null;
  }

  // Email preferences page is accessible to both authenticated and unauthenticated users
  // It uses token-based auth from email links
  if (landingPage === 'email-preferences') {
    return (
      <EmailPreferencesView
        token={emailPrefsToken || undefined}
        onBack={handleBackToLanding}
        onHome={() => {
          window.location.href = '/';
        }}
        lang={landingLang}
      />
    );
  }

  if (!isAuthenticated) {
    if (landingPage === 'login') {
      return (
        <LoginView
          onBack={handleBackToLanding}
          onRegister={() => setLandingPage('register')}
          onSuccess={handleAuthSuccess}
          lang={landingLang}
        />
      );
    }
    if (landingPage === 'register') {
      return (
        <RegisterView
          onBack={handleBackToLanding}
          onLogin={() => setLandingPage('login')}
          onSuccess={handleAuthSuccess}
          lang={landingLang}
        />
      );
    }
    if (landingPage === 'privacy') {
      return (
        <PrivacyView 
          onBack={handleBackToLanding} 
          lang={landingLang}
          onLangChange={setLandingLang}
          onHome={handleBackToLanding}
        />
      );
    }
    if (landingPage === 'terms') {
      return (
        <TermsView
          onBack={handleBackToLanding}
          lang={landingLang}
          onLangChange={setLandingLang}
          onHome={handleBackToLanding}
        />
      );
    }
    if (landingPage === 'email-preferences') {
      return (
        <EmailPreferencesView
          token={emailPrefsToken || undefined}
          onBack={handleBackToLanding}
          onHome={handleBackToLanding}
          lang={landingLang}
        />
      );
    }
    return (
      <LandingView
        onLogin={handleLogin}
        onRegister={handleRegister}
        onShowPrivacy={handleShowPrivacy}
        onShowTerms={handleShowTerms}
        lang={landingLang}
        onLangChange={setLandingLang}
      />
    );
  }

  const needsConsent = user && !user.termsAcceptedAt && !consentsAccepted;

  if (needsConsent) {
    if (consentPage === 'privacy') {
      return <PrivacyView onBack={() => setConsentPage('consent')} lang={landingLang} onLangChange={setLandingLang} onHome={() => setConsentPage('consent')} />;
    }
    if (consentPage === 'terms') {
      return <TermsView onBack={() => setConsentPage('consent')} lang={landingLang} onLangChange={setLandingLang} onHome={() => setConsentPage('consent')} />;
    }
    return (
      <ConsentGate
        lang={landingLang}
        onAccept={async (preferredLanguage: string) => { 
          setLandingLang(preferredLanguage as 'en' | 'it' | 'es' | 'de' | 'fr');
          setConsentsAccepted(true); 
          await qc.refetchQueries({ queryKey: ['/api/auth/user'] }); 
        }}
        onShowPrivacy={() => setConsentPage('privacy')}
        onShowTerms={() => setConsentPage('terms')}
      />
    );
  }

  const handleOnboardingFinish = async (d: LifeContext) => {
    const newContext = { ...d, setupComplete: true };
    
    // Show hydration loading overlay immediately
    setIsHydratingPostOnboarding(true);
    setHydrationProgress(10);
    setCurrentScreen('pulse');
    
    // Set context locally first for immediate UI feedback
    setLifeContext(newContext);
    setHydrationProgress(20);

    try {
      // Save context with ALL fields including aiProfile, keyPeople, keyPlaces
      // IMPORTANT: Use mutateAsync and await to ensure data is saved before fetching
      await saveContextMutation.mutateAsync({
        userName: newContext.userName,
        goals: newContext.goals,
        values: newContext.values,
        blockers: newContext.blockers,
        setupComplete: true,
        language: newContext.language,
        aiPersonality: newContext.aiPersonality,
        aiProfile: newContext.aiProfile,
        keyPeople: newContext.keyPeople,
        keyPlaces: newContext.keyPlaces,
      });
      setHydrationProgress(40);

      // 🔄 Poll/retry until data is ready
      // NOTE: Entries come from bulk photo upload only (questionnaire creates AIProfile, not entries)
      // So we don't require a minimum number of entries - user might not upload any photos
      const maxRetries = 10;
      const retryDelay = 1500; // 1.5 seconds between retries
      let retryCount = 0;
      let dataReady = false;
      let finalEntries: any[] = [];
      let finalContext: any = null;
      let finalDecisions: any[] = [];
      let finalPatterns: any[] = [];

      while (retryCount < maxRetries && !dataReady) {
        setHydrationProgress(40 + Math.min(retryCount * 5, 45));

        // Invalidate cache to force fresh fetch
        await qc.invalidateQueries({ queryKey: ['/api/entries'] });
        await qc.invalidateQueries({ queryKey: ['/api/context'] });
        await qc.invalidateQueries({ queryKey: ['/api/patterns'] });
        await qc.invalidateQueries({ queryKey: ['/api/decisions'] });

        const [entriesResult, contextResult, decisionsResult, patternsResult] = await Promise.all([
          qc.fetchQuery({ queryKey: ['/api/entries'] }),
          qc.fetchQuery({ queryKey: ['/api/context'] }),
          qc.fetchQuery({ queryKey: ['/api/decisions'] }),
          qc.fetchQuery({ queryKey: ['/api/patterns'] }),
        ]);

        // Extract entries from paginated response { entries, total, hasMore } or direct array
        if (entriesResult?.entries && Array.isArray(entriesResult.entries)) {
          finalEntries = entriesResult.entries;
        } else if (Array.isArray(entriesResult)) {
          finalEntries = entriesResult;
        } else {
          finalEntries = [];
        }
        finalContext = contextResult;
        finalDecisions = Array.isArray(decisionsResult) ? decisionsResult : [];
        finalPatterns = Array.isArray(patternsResult) ? patternsResult : [];

        // Check if critical data is ready:
        // - Context must have userName (from onboarding)
        // - If there are entries, they should have AI insights
        // - Patterns are generated CLIENT-SIDE after app loads, so we don't wait for them
        const hasContext = finalContext?.userName;
        const hasEntries = finalEntries.length > 0;

        // Count entries with AI insights (aiInsight is added during entry ingestion)
        const entriesWithAI = finalEntries.filter((e: any) =>
          e.aiInsight && e.aiInsight.length > 10
        );
        const allEntriesHaveAI = !hasEntries || entriesWithAI.length >= finalEntries.length;
        const mostEntriesHaveAI = !hasEntries || entriesWithAI.length >= Math.max(1, finalEntries.length - 1);

        // Data is ready when:
        // - Context has userName
        // - AND either no entries OR all entries have AI insights
        if (hasContext && allEntriesHaveAI) {
          dataReady = true;
          console.log(`✅ Post-onboarding data ready: ${finalEntries.length} entries, context loaded`);
        }
        // Accept "most entries have AI" after 5 retries (~7.5s) to avoid long waits
        else if (retryCount >= 5 && hasContext && mostEntriesHaveAI) {
          dataReady = true;
          console.log(`✅ Post-onboarding data ready (partial): ${finalEntries.length} entries, ${entriesWithAI.length} with AI`);
        }
        // Keep polling until max retries
        else {
          retryCount++;
          console.log(`⏳ Post-onboarding retry ${retryCount}/${maxRetries}: ${finalEntries.length} entries, ${entriesWithAI.length} with AI, context: ${hasContext}`);
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          } else {
            // After max retries, accept what we have - user shouldn't wait forever
            console.log(`⚠️ Post-onboarding max retries reached, proceeding with available data`);
            dataReady = true;
          }
        }
      }

      setHydrationProgress(90);

      // Directly hydrate local state using shared mappers for consistency
      if (finalEntries.length > 0) {
        setEntries(mapDbEntriesToState(finalEntries));
      }

      if (finalDecisions.length > 0) {
        setDecisions(mapDbDecisionsToState(finalDecisions));
      }

      if (finalPatterns.length > 0) {
        setPatterns(finalPatterns);
      }

      // Update context using shared mapper for consistency
      if (finalContext) {
        const mappedContext = mapDbContextToState(finalContext);
        setLifeContext({ ...mappedContext, setupComplete: true });
      }

      setHydrationProgress(100);
      
      // Small delay to show 100% before hiding
      await new Promise(resolve => setTimeout(resolve, 300));

    } catch (error) {
      console.error('Onboarding hydration error:', error);
      // Even on error, we have the local context set, so user can still use the app
      // The data will load on next page refresh
    } finally {
      // ALWAYS clean up overlay state to prevent user lock
      setDataLoaded(true);
      setIsHydratingPostOnboarding(false);
      setHydrationProgress(0);
    }
  };

  const handleContextUpdate = (ctx: LifeContext) => {
    setLifeContext(ctx);
    if (ctx.setupComplete) {
      saveContextMutation.mutate({
        userName: ctx.userName,
        goals: ctx.goals,
        values: ctx.values,
        blockers: ctx.blockers,
        setupComplete: ctx.setupComplete,
        language: ctx.language,
        aiPersonality: ctx.aiPersonality,
        aiProfile: ctx.aiProfile,
        keyPeople: ctx.keyPeople,
        keyPlaces: ctx.keyPlaces,
      });
    }
  };

  // Prevent showing onboarding briefly while loading authenticated user's data
  // Use unified sync overlay for consistent loading experience
  if (isAuthenticated && !dataLoaded) {
    return (
      <AnalysisLoadingOverlay
        isVisible={true}
        type="sync"
        progress={syncProgress}
        language={loadingLanguage}
      />
    );
  }

  // Hydration loading messages in all languages
  const hydrationMessages: Record<string, { title: string; subtitle: string }> = {
    Italian: { title: 'Preparando il tuo spazio...', subtitle: 'Stiamo caricando i tuoi dati' },
    English: { title: 'Preparing your space...', subtitle: 'Loading your data' },
    Spanish: { title: 'Preparando tu espacio...', subtitle: 'Cargando tus datos' },
    German: { title: 'Bereite deinen Bereich vor...', subtitle: 'Lade deine Daten' },
    French: { title: 'Préparation de votre espace...', subtitle: 'Chargement de vos données' },
  };
  const hydrationMsg = hydrationMessages[lifeContext.language] || hydrationMessages.English;

  return (
    <LanguageProvider language={lifeContext.language}>
      <FeatureDiscoveryProvider 
        discoveryState={lifeContext.featureDiscovery || {}} 
        onUpdateDiscovery={updateFeatureDiscovery}
      >
        <div className="flex flex-col h-screen h-dvh bg-white w-full relative font-sans overflow-hidden">
          {/* Post-onboarding hydration loading overlay */}
          {isHydratingPostOnboarding && (
            <div className="fixed inset-0 z-[99999] bg-white flex flex-col items-center justify-center px-8">
              <div className="flex flex-col items-center gap-8 max-w-sm w-full">
                {/* Animated icon */}
                <div className="relative">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 shadow-2xl shadow-indigo-500/30 animate-pulse" />
                  <div className="absolute inset-0 w-20 h-20 rounded-3xl border-2 border-indigo-300 animate-ping opacity-40" />
                </div>
                
                {/* Text */}
                <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">{hydrationMsg.title}</h2>
                  <p className="text-gray-500">{hydrationMsg.subtitle}</p>
                </div>
                
                {/* Progress bar */}
                <div className="w-full max-w-xs">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${hydrationProgress}%` }}
                    />
                  </div>
                  <p className="text-center text-sm text-gray-400 mt-3">{hydrationProgress}%</p>
                </div>
                
                {/* Animated dots */}
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-indigo-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <main className="flex-1 relative overflow-y-auto pb-20">
            {currentScreen === 'onboarding' ? <OnboardingView onFinish={handleOnboardingFinish} initialLanguage={landingToAppLanguage[landingLang]} /> :
             currentScreen === 'pulse' ? <HomeView context={lifeContext} entries={effectiveEntries} patterns={patterns} decisions={decisions} isLoading={isDataLoading} entriesSynced={entriesSynced} onVoiceTap={() => {}} onNavigate={setCurrentScreen} onNewEntry={captureMoment} onPersonalityChange={handlePersonalityChange} /> :
             currentScreen === 'archive' ? <JournalView context={lifeContext} entries={effectiveEntries} entriesLoading={!entriesSynced || entriesLoading} onDeleteEntry={deleteEntry} onLikeEntry={handleLikeEntry} alignmentFilter={alignmentFilter} onClearFilter={() => setAlignmentFilter(null)} onNavigate={setCurrentScreen} onPersonalityChange={handlePersonalityChange} /> :
             currentScreen === 'consult' ? <AskView context={lifeContext} entries={effectiveEntries} decisions={decisions} patterns={patterns} onNewEntry={captureMoment} setDecisions={handleSetDecisions} onNavigate={setCurrentScreen} onAddValue={handleAddValue} onAddGoal={handleAddGoal} onPersonalityChange={handlePersonalityChange} /> :
             currentScreen === 'decisions' ? <DecisionsView context={lifeContext} entries={effectiveEntries} decisions={decisions} setDecisions={handleSetDecisions} onResolve={resolveDecision} onDelete={deleteDecision} onNavigate={setCurrentScreen} onPersonalityChange={handlePersonalityChange} /> :
             currentScreen === 'profile' ? <MyProfileView context={lifeContext} onNavigate={setCurrentScreen} /> :
             currentScreen === 'evolution' ? <EvolutionHistoryView context={lifeContext} onNavigate={setCurrentScreen} /> :
             <InsightsView context={lifeContext} entries={effectiveEntries} patterns={patterns} decisions={decisions} onUpdateContext={handleContextUpdate} onRefreshPatterns={triggerPatternAnalysis} onNavigate={setCurrentScreen} onNavigateWithFilter={handleNavigateWithFilter} onPersonalityChange={handlePersonalityChange} />
            }
          </main>

      {currentScreen !== 'onboarding' && (
        <nav className="fixed bottom-0 left-0 right-0 z-[9999] bg-white/95 backdrop-blur-2xl border-t border-black/[0.04]" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}>
          {/* Mobile: compact icons only */}
          <div className="lg:hidden">
            <div className="flex items-center justify-around h-14 max-w-lg mx-auto px-2">
              {[
                { id: 'pulse', icon: LayoutGrid },
                { id: 'archive', icon: BookOpen },
                { id: 'consult', icon: MessageSquare },
                { id: 'decisions', icon: Compass },
                { id: 'insights', icon: BarChart3 }
              ].map((item) => {
                const isActive = currentScreen === item.id;
                return (
                  <button 
                    key={item.id} 
                    onClick={() => setCurrentScreen(item.id as Screen)} 
                    className="relative flex items-center justify-center w-14 h-14 transition-all duration-200 active:scale-90"
                  >
                    <div className={`flex items-center justify-center w-10 h-10 rounded-2xl transition-all duration-300 ${isActive ? 'bg-[#1D1D1F]' : 'bg-transparent'}`}>
                      <item.icon 
                        size={22} 
                        strokeWidth={isActive ? 2.5 : 1.8} 
                        className={`transition-colors duration-200 ${isActive ? 'text-white' : 'text-black/40'}`}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Desktop: icons with labels, anchored at bottom */}
          <div className="hidden lg:block">
            <div className="flex items-center justify-center gap-2 h-16 max-w-4xl mx-auto px-4">
              {[
                { id: 'pulse', icon: LayoutGrid, label: t.navHome },
                { id: 'archive', icon: BookOpen, label: t.navDiary },
                { id: 'consult', icon: MessageSquare, label: t.navAsk },
                { id: 'decisions', icon: Compass, label: t.navDecisions },
                { id: 'insights', icon: BarChart3, label: t.navAnalysis }
              ].map((item) => {
                const isActive = currentScreen === item.id;
                return (
                  <button 
                    key={item.id} 
                    onClick={() => setCurrentScreen(item.id as Screen)} 
                    className={`relative flex items-center gap-2.5 px-6 py-3 rounded-2xl transition-all duration-300 ${isActive ? 'bg-[#1D1D1F] shadow-lg shadow-black/20' : 'hover:bg-gray-100'}`}
                  >
                    <item.icon 
                      size={20} 
                      strokeWidth={isActive ? 2.5 : 1.8} 
                      className={`transition-colors duration-200 ${isActive ? 'text-white' : 'text-black/40'}`}
                    />
                    <span className={`text-[13px] font-bold transition-colors duration-200 ${isActive ? 'text-white' : 'text-black/40'}`}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}
        </div>
      </FeatureDiscoveryProvider>
    </LanguageProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <MainApp />
    </QueryClientProvider>
  );
};

export default App;
